import { FormEvent, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button } from '../components';
import { FormInput } from '../components/forms/FormInput';
import { PasswordInput } from '../components/forms/PasswordInput';
import { RequestPasswordReset } from '../components/buttons/PasswordReset';
import { getMultiFactorResolver, type MultiFactorError } from 'firebase/auth';
import * as Sentry from '@sentry/browser';
import { auth } from '../constants/firebaseModular';
import { MfaChallengeForm } from '../components/auth/MfaChallengeForm';
import type { Resolver } from '../hooks/useMfaChallenge';

export const Route = createFileRoute('/enroll')({
  component: Enroll,
});

function Enroll() {
  // LOCAL STATE FOR USERNAME AND PASSWORD
  const [email, setEmail] = useState(window.localStorage.getItem('initialEmail') || '');
  const [password, setPassword] = useState('');
  const [working, setWorking] = useState(false);
  /**
   * The pending MFA challenge. LOCAL STATE ONLY — the resolver is a live object
   * holding the in-flight sign-in and does not survive serialization. See the
   * fuller note in `login.tsx`.
   */
  const [mfaResolver, setMfaResolver] = useState<Resolver | null>(null);

  // AUTH HANDLING
  const { firebaseLogin } = useAuth();
  const { currentUser } = useAuth();
  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setWorking(true);
    try {
      await firebaseLogin(email, password);
    } catch (error) {
      // ⚠️ SAME LOCKOUT AS login.tsx, AND FOR THE SAME REASON. This is a full
      // email+password sign-in, so a partner who enrols a second factor hits
      // `auth/multi-factor-auth-required` here too — thrown when the password
      // was CORRECT. The blanket catch below would tell them their login
      // failed, permanently (CU-86ajwpnek §7).
      const code = (error as { code?: string })?.code ?? '';
      if (code === 'auth/multi-factor-auth-required') {
        try {
          setMfaResolver(getMultiFactorResolver(auth, error as MultiFactorError) as unknown as Resolver);
          Sentry.addBreadcrumb({
            category: 'mfa-sms',
            level: 'info',
            message: 'enroll: second factor required',
          });
        } catch (resolverError) {
          Sentry.captureException(resolverError, {
            tags: { feature: 'mfa-sms', step: 'enroll-login-resolver' },
          });
          alert(
            "This account needs an extra verification step, but we couldn't start it. Please try again, or contact info@mideohealth.com.",
          );
          setWorking(false);
        }
        return;
      }

      alert(
        "We couldn't log you in. Please try again. Or if you're having trouble, please reach out to info@mideohealth.com.",
      );
      setWorking(false);
    }
  };

  // NAVIGATION FROM TANSTACK ROUTER
  const navigate = useNavigate();

  // LISTEN FOR COMPONENT UPDATE
  useEffect(() => {
    // HANDLE REDIRECT TO REQUESTED URL
    // sessionStorage: set by $patientId UID mismatch guard (tab-scoped, auto-expires)
    // localStorage: set by _authenticated guard (persists across tabs/reloads)
    const requestedURL = sessionStorage.getItem('requestedURL') || window.localStorage.getItem('requestedURL');
    if (currentUser && requestedURL) {
      // ACCOUNT FOR DIFFERENCE BETWEEN PROD AND STAGING URLS
      const path = requestedURL.includes('com') ? requestedURL.split('com')[1] : requestedURL.split('app')[1];
      sessionStorage.removeItem('requestedURL');
      window.localStorage.removeItem('requestedURL');

      // Safety check: if the requestedURL contains a patientId that doesn't match
      // the logged-in user, don't redirect there (it would trigger the $patientId
      // UID mismatch guard and bounce them back in a loop).
      const pathSegments = path?.split('/').filter(Boolean);
      const urlPatientId = pathSegments?.[0];
      if (urlPatientId && urlPatientId !== currentUser.uid) {
        navigate({
          to: `/${currentUser.uid}`,
          from: '/enroll',
        });
      } else {
        navigate({
          to: path,
          from: '/enroll',
        });
      }
    } else if (currentUser?.uid && !requestedURL) {
      navigate({
        to: `/${currentUser.uid}`,
        from: '/enroll',
      });
    }
  }, [currentUser, navigate]);

  // ⚠️ The challenge REPLACES the sign-in form rather than stacking over it —
  // leaving the password step mounted invites a second submit against a
  // resolver already pending. `currentUser` is still null here, so the redirect
  // effect above has not run.
  if (mfaResolver) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-6 px-4">
        <img
          src="https://storage.googleapis.com/mideoid-console-public/logos/MIDEOhealth.png"
          alt="mideo_logo"
          className="h-24"
        />
        <MfaChallengeForm
          resolver={mfaResolver}
          // CU-86akbcqv8 — the address typed at the password step, so a
          // challenge-leg failure can be tied to an attempt. ⚠️ A CLAIM, not a
          // verified identity: the second factor is unproven at this point.
          claimedEmail={email}
          onComplete={() => {
            // `requestedURL` is left ALONE — onAuthStateChanged fires now and
            // the existing redirect effect consumes it as on an ordinary login.
            setMfaResolver(null);
            setPassword('');
          }}
          onCancel={() => {
            setMfaResolver(null);
            setPassword('');
            setWorking(false);
          }}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex h-screen flex-col items-center justify-center">
        <img
          src="https://storage.googleapis.com/mideoid-console-public/logos/MIDEOhealth.png"
          alt="mideo_logo"
          className="h-36"
        />
        <div className="my-4 px-8">
          <p className="text-xl font-bold">
            This page is only intended for our corporate/organization partners with registered email addresses.
          </p>
        </div>
        <div className="my-2 px-8">
          <p className="text-xl font-bold">
            ALERT: Due to network restrictions, it is important that you avoid using your corporate network and/or a
            corporate-issued device to access the MIDEO Portal. If you experience an issue, please try accessing the
            MIDEO Portal from a personal device at home.
          </p>
        </div>
        <form>
          <FormInput
            id="email"
            handleBlur={() => {}}
            label="Email"
            name="username"
            value={email}
            handleChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
          />
          <PasswordInput
            id="password"
            title="Password"
            password={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <div className="flex flex-col">
            <Button
              id="submit"
              type="submit"
              onClick={(e) => handleLogin(e)}
              textContent="Login"
              disabled={working}
              working={working}
              workingText="Logging in..."
            />
            <RequestPasswordReset email={email} />
          </div>
        </form>
      </div>
    </div>
  );
}
